import {
  getRelativePosition,
  useGetMultiplePrint,
  useUpdateMultiplePrint,
} from "@/api/multipleprint/multipleprint";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import MultiplePrintSingleDrawer from "@/components/multipleprint/MultiplePrintSingleDrawer";
import { Multipleprint } from "@/lib/types";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetAttachmentsByDocId } from "@/api/attachment";
import { useGetAllAuthors } from "@/api/author/author";
import {
  appendNoDuplicateRecordNumberRefine,
  multipleprintSchema,
} from "@/api/multipleprint/multipleprint-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { DateQualifierFields } from "@/components/form-items/DateQualifierFields";
import {
  FourDimensionalInput,
  TwoDimensionalInput,
} from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import {
  DoubleMultilingualField,
  MultilingualField,
} from "@/components/form-items/MultilingualField";
import LinksManagement from "@/components/LinksManagement";
import PortfolioItemsLinks from "@/components/links/PortfolioItemsLinks";
import { PartOfPortfoliosLine } from "@/components/multiprint/PartOfPortfoliosLine";
import AddFileModal from "@/components/modal/AddFileModal";
import ExemplaireDashboard from "@/components/multiprint/exemplaires/ExemplaireDashboard";
import { MultipleprintHistoryTable } from "@/components/multiprint/MultipleprintHistoryTable";
import { useAuth } from "@/context/auth/AuthContext";
import { DashboardParamsProviders } from "@/context/DashboardParamsProviders";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldMultipleprintSummary } from "@/lib/computeFields";
import { appendFormattedDateToTitle } from "@/lib/date-qualifiers";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { tWithKey } from "@/lib/i18n-fallback";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  multipleprintNatureFieldLabel,
  multipleprintNatureLabel,
  multipleprintNatureOptions,
} from "@/lib/multipleprint-nature";
import { BadgeCell } from "@/components/table/CustomCell";

const schema = appendNoDuplicateRecordNumberRefine(
  multipleprintSchema.passthrough(),
);

const EditMultipleprint = ({
  multipleprint,
}: {
  multipleprint: Multipleprint;
}) => {
  // Rafraîchit le tableau des fichiers après un ajout réussi dans AddFileModal.
  const [refreshFiles, setRefreshFiles] = useState(false);

  const { instanceData } = useAuth();

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const provenanceMode = instanceData?.provenanceMode ?? false;

  const defaultValues = useMemo(() => {
    return {
      ...multipleprint,
      estampesMultiplesDescriptionNotesPubliquesLabelPublished:
        multipleprint.estampesMultiplesDescriptionNotesPubliquesLabelPublished ??
        true,
      estampesMultiplesNature: multipleprint.estampesMultiplesNature ?? "simple",
      relatedAuthorIds: multipleprint.relatedAuthor.map((author) => author.id),
    };
  }, [multipleprint]);

  const form = useForm({
    defaultValues,
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { data: attachments } = useGetAttachmentsByDocId(multipleprint.id);
  // TODO: apply MultiSelect search
  const { data: authors } = useGetAllAuthors({
    where: {},
    order: { artistesAuteursDescriptionNom: "asc" },
  });

  const queryClient = useQueryClient();
  const [, setSearchParam] = useSearchParams();

  const multipleprintUpdater = useUpdateMultiplePrint();

  const { relativePosition } = useDashboardParams<Multipleprint>({
    statusField: "estampesMultiplesPublicationStatus",
    resourceName: "multipleprint",
    relativePosition: {
      currentId: multipleprint.id,
      positionFetcher: getRelativePosition,
    },
  });

  const { t, i18n } = useTranslation();
  const vt = useVisibleT();

  const handleResume = (data: Multipleprint) => {
    return computeFieldMultipleprintSummary(data, {
      language: getMainLanguage(),
      lineBreak: true,
      translation: t,
    });
  };

  // Portfolio : ses multiples simples se gèrent dans l'onglet Portfolio.
  const isPortfolio = form.watch("estampesMultiplesNature") === "portfolio";

  const shouldDisplayVolume =
    form.watch("estampesMultiplesDescriptionDimensionsProfondeur") || false;

  const mainLanguage = getMainLanguage();
  const estampesMultiplesDescriptionTypeMultilingual = form.watch(
    "estampesMultiplesDescriptionTypeMultilingual",
  );
  const multipleprintType =
    estampesMultiplesDescriptionTypeMultilingual?.[
      mainLanguage as keyof typeof estampesMultiplesDescriptionTypeMultilingual
    ] || "";
  const estampesMultiplesDescriptionNumeroEnregistrement = form.watch(
    "estampesMultiplesDescriptionNumeroEnregistrement",
  );
  const registrationLabel = t("backoffice.header.registrationNumber.label");
  const registrationReference = useMemo(
    () =>
      formatFormRegistrationReference(
        estampesMultiplesDescriptionNumeroEnregistrement,
        {
          label: registrationLabel,
          locale: i18n.language,
          suffix: multipleprintType,
        },
      ),
    [
      estampesMultiplesDescriptionNumeroEnregistrement,
      multipleprintType,
      registrationLabel,
      i18n.language,
    ],
  );

  return (
    <FormLayout
      formKey="multipleprint"
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            multipleprintUpdater.mutate(
              {
                ...(values as Multipleprint),
                ...(status && { estampesMultiplesPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("multipleprintSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ventesDescriptionNumeroVente != undefined)
              setSearchParam({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["multipleprint", multipleprint.id],
        });
        queryClient.invalidateQueries({ queryKey: ["multipleprints"] });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: multipleprint.id }],
        });
      }}
      publicationStatus={multipleprint.estampesMultiplesPublicationStatus}
      badges={
        multipleprint.estampesMultiplesNature === "portfolio" && (
          <BadgeCell
            value={multipleprintNatureLabel(t, "portfolio")}
            variant="outlined"
          />
        )
      }
      referenceExtra={
        multipleprint.estampesMultiplesNature !== "portfolio" && (
          <PartOfPortfoliosLine portfolios={multipleprint.inPortfolios ?? []} />
        )
      }
      reference={registrationReference}
      title={appendFormattedDateToTitle(
        multipleprint.estampesMultiplesDescriptionTitre,
        multipleprint.estampesMultiplesDescriptionDateDeCreation,
        {
          unknownDate:
            multipleprint.estampesMultiplesDescriptionDateDeCreationInconnue,
          uncertainDate:
            multipleprint.estampesMultiplesDescriptionDateDeCreationIncertaine,
          estimatedDate:
            multipleprint.estampesMultiplesDescriptionDateDeCreationEstimee,
        },
      )}
      id={multipleprint.id}
      type="multipleprint"
      linkOverview={`/edit/multipleprint/overview/${multipleprint.id}`}
      relativePosition={relativePosition}
      picturesData={
        attachments && {
          pictures: attachments,
          type: "multipleprint",
        }
      }
      print={<MultiplePrintSingleDrawer multipleprint={multipleprint} />}
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        formKey="multipleprint"
        tabs={[
          { value: "description", label: t("tab_description"), nonHideable: true },
          { value: "dimensions-poids", label: t("multipleDimensionsPoids") },
          {
            value: "localisation-historique",
            label: provenanceMode
              ? t("multipleprint.tabs.provenance")
              : t("multipleprint.tabs.historique"),
          },
          { value: "matrice", label: t("tab_multiple_matrice") },
          {
            value: "photos-credit-copyright",
            label: t("tab_multiplePhotosCreditCopyright"),
          },
          { value: "epreuveTirage", label: t("epreuves") },
          { value: "sources", label: t("sources") },
          { value: "files", label: t("fichiers") },
          {
            value: "portfolio",
            label: tWithKey(t, "multipleprint.tabs.portfolio", "Portfolio"),
            hide: !isPortfolio,
          },
          { value: "links", label: t("linkedEntries") },
          { value: "exemplaires", label: t("exemplaires") },
        ]}
      >
        <AF.Tab value="description" className="flex-col">
          <AF.Row className="items-start">
            <AF.Col className="w-1/2">
              <AF.Text
                name="estampesMultiplesDescriptionNumeroEnregistrement"
                nonMaskable
              />
              <AF.SelectField
                name="estampesMultiplesNature"
                label={multipleprintNatureFieldLabel(t)}
                options={multipleprintNatureOptions(t)}
                nonMaskable
              />
              <AF.Text
                name="estampesMultiplesDescriptionNumeroInventaire"
                corner={() => (
                  <AF.Switch
                    name="estampesMultiplesDescriptionNumeroInventairePublished"
                    labelFirst
                  />
                )}
              />
              <AF.Text
                name="estampesMultiplesDescriptionNumeroAncien"
                tootltip={t("multiple.description.tooltip.numeroAncien")}
                corner={() => (
                  <AF.Switch
                    name="estampesMultiplesDescriptionNumeroAncienPublished"
                    labelFirst
                  />
                )}
              />
              <AF.Text
                name="estampesMultiplesDescriptionNumeroAutres"
                tootltip={t("multiple.description.tooltip.numeroAutres")}
                corner={() => (
                  <AF.Switch
                    name="estampesMultiplesDescriptionNumeroAutresPublished"
                    labelFirst
                  />
                )}
              />
              <AF.MultiSelectField
                name="relatedAuthorIds"
                options={
                  authors?.map((author) => ({
                    label: `${author.artistesAuteursDescriptionPrenom} ${author.artistesAuteursDescriptionNom}`,
                    value: author.id,
                  })) ?? []
                }
                label={t("estampesMultiplesDescriptionAuteurArtiste")}
                placeholder={t("select")}
              />
              <AF.RadioGroup
                groupName="estampesMultiplesDescriptionAuteurArtisteOptionsGroup"
                label={t("oeuvresDescriptionAttributionOptionsGroup")}
                resettable={true}
                items={[
                  {
                    value: "estampesMultiplesDescriptionAuteurArtisteAttribueA",
                  },
                  { value: "estampesMultiplesDescriptionAuteurArtisteAnonyme" },
                  { value: "estampesMultiplesDescriptionAuteurArtisteDApres" },
                  {
                    value:
                      "estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe",
                  },
                  {
                    value:
                      "estampesMultiplesDescriptionAuteurArtisteEntourageDe",
                  },
                  { value: "estampesMultiplesDescriptionAuteurAtelierDe" },
                ]}
              />
              <AF.Text name="estampesMultiplesDescriptionTitre" nonMaskable />
              <AF.Text name="estampesMultiplesDescriptionAutreTitre" />
              <AF.Text name="estampesMultiplesDescriptionSousTitre" />
              <AF.RadioGroup
                groupName="estampesMultiplesDescriptionTitreOptionsGroup"
                label={tWithKey(
                  t,
                  "estampesMultiplesDescriptionTitreOptionsGroup",
                  "Options de titre",
                )}
                items={[
                  { value: "estampesMultiplesDescriptionTitreSansTitre" },
                  { value: "estampesMultiplesDescriptionTitreInconnu" },
                  { value: "estampesMultiplesDescriptionTitreIncertin" },
                ]}
              />
              <Separator />
              {
                // TODO: OEUVRES DANS ENSEMBLE
              }
              <AF.Col>
                <MultilingualField
                  name="estampesMultiplesDescriptionTypeMultilingual"
                  render={(lang) => (
                    <ModifiableList
                      name={`estampesMultiplesDescriptionTypeMultilingual.${lang}`}
                      listKey="liste-types-oeuvres"
                      lang={lang}
                      label={t("estampesMultiplesDescriptionType")}
                    />
                  )}
                />
                <AF.Checkbox
                  name="estampesMultiplesDescriptionOeuvreDoubleFace"
                  align={true}
                />
              </AF.Col>
              <DateQualifierFields
                names={{
                  date: "estampesMultiplesDescriptionDateDeCreation",
                  unknown: "estampesMultiplesDescriptionDateDeCreationInconnue",
                  uncertain:
                    "estampesMultiplesDescriptionDateDeCreationIncertaine",
                  estimated:
                    "estampesMultiplesDescriptionDateDeCreationEstimee",
                }}
              />

              <ModifiableList
                name="estampesMultiplesDescriptionEditeur"
                listKey="liste-multipleprint-editeur"
              />
              <ModifiableList
                name="estampesMultiplesDescriptionFabricantImprimeur"
                listKey="liste-multipleprint-imprmeur"
              />
            </AF.Col>
            <AF.Col className="w-1/2">
              <AF.Row>
                <AF.Checkbox name="estampesMultiplesDescriptionNumerisationAFaire" />
                <AF.Checkbox name="estampesMultiplesDescriptionPhotoWebReady" />
                <AF.Checkbox name="estampesMultiplesDescriptionPhotoHdReady" />
              </AF.Row>
              <AF.ModifiableMultiSelectField
                name="estampesMultiplesDescriptionMotsCles"
                listKey="liste-mots-cles-multipleprint"
                maxCount={null}
                description={vt(
                  "bo.fieldHelp.motsCles",
                  "Les mots-clés sont utilisés pour la recherche sur le site public.",
                )}
              />
              <AF.RichText
                name="estampesMultiplesDescriptionResume"
                computeAction={handleResume}
              />
              <AF.RichText name="estampesMultiplesDescriptionNotesTravail" />

              <AF.Switch
                name="estampesMultiplesDescriptionNotesPubliquesLabelPublished"
                labelFirst
                maskable
              />

              <MultilingualField
                name="estampesMultiplesDescriptionNotesPubliquesMultilingual"
                render={(lang) => (
                  <AF.RichText
                    name={`estampesMultiplesDescriptionNotesPubliquesMultilingual.${lang}`}
                    label={t("estampesMultiplesDescriptionNotesPubliques")}
                  />
                )}
              />
            </AF.Col>
          </AF.Row>
          <Separator />
          <AF.Col>
            <span>{t("forms.multilingual-fields.indication")}</span>

            <AF.Row className="items-start">
              <DoubleMultilingualField
                className="w-full"
                // fixed={getMainLanguage()}
                render={(lang) => (
                  <AF.Col>
                    <ModifiableList
                      label={t("estampesMultiplesDescriptionTechnique")}
                      name={`estampesMultiplesDescriptionTechnique.${lang}`}
                      listKey={`liste-techniques`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("estampesMultiplesDescriptionSupportMateriau")}
                      name={`estampesMultiplesDescriptionSupportMateriau.${lang}`}
                      listKey={`liste-supports`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t(
                        "estampesMultiplesDescriptionSignatureLocalisation",
                      )}
                      name={`estampesMultiplesDescriptionSignatureLocalisation.${lang}`}
                      listKey="liste-signatures"
                      lang={lang}
                    />
                    <AF.Text name="estampesMultiplesDescriptionSignatureTranscription" />
                    <ModifiableList
                      label={t("estampesMultiplesDescriptionInscriptionsDos")}
                      name={`estampesMultiplesDescriptionInscriptionsDos.${lang}`}
                      listKey="liste-inscriptions-dos"
                      lang={lang}
                      corner={() => (
                        <AF.Switch
                          name={`estampesMultiplesDescriptionInscriptionsDosPublished.${lang}`}
                          label={t(
                            "estampesMultiplesDescriptionInscriptionsDosPublished",
                          )}
                        />
                      )}
                    />
                  </AF.Col>
                )}
              />
            </AF.Row>
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="dimensions-poids">
          <AF.Col>
            <AF.Col className="w-1/2">
              <FourDimensionalInput
                parentName="estampesMultiplesDescriptionDimensionsOeuvre"
                needSurface={!shouldDisplayVolume}
                needVolume={shouldDisplayVolume}
              />
            </AF.Col>
            <AF.Col className="w-1/2">
              <AF.RadioGroup
                groupName="estampesMultiplesDescriptionDimensionsOeuvreOptionsGroup"
                label={t("oeuvresDescriptionDimensionsOptionsGroup")}
                resettable={true}
                items={[
                  {
                    value:
                      "estampesMultiplesDescriptionDimensionsOeuvreVerifiees",
                  },
                  {
                    value:
                      "estampesMultiplesDescriptionDimensionsOeuvreInconnues",
                  },
                  {
                    value:
                      "estampesMultiplesDescriptionDimensionsOeuvreDouteuses",
                  },
                ]}
              />
            </AF.Col>
            <AF.Col className="w-1/2">
              <AF.Checkbox name="estampesMultiplesDescriptionDimensionsProfondeur" />
            </AF.Col>
            <AF.Col className="w-1/2">
              <TwoDimensionalInput parentName="estampesMultiplesDescriptionDimensionsImage" />
            </AF.Col>
            <AF.Col className="w-1/2">
              <AF.RadioGroup
                groupName="estampesMultiplesDescriptionDimensionsImageOptionsGroup"
                label={t("oeuvresDescriptionDimensionsOptionsGroup")}
                resettable={true}
                items={[
                  {
                    value:
                      "estampesMultiplesDescriptionDimensionsImageVerifiees",
                  },
                  {
                    value:
                      "estampesMultiplesDescriptionDimensionsImageInconnues",
                  },
                  {
                    value:
                      "estampesMultiplesDescriptionDimensionsImageDouteuses",
                  },
                ]}
              />
            </AF.Col>
            <AF.Col className="w-1/2">
              <AF.Text
                name="estampesMultiplesDescriptionPoidsOeuvre"
                unit="kg"
                className="w-20"
              />
              <AF.Text
                name="estampesMultiplesDescriptionDimensionsOeuvreRaw"
                label={t("estampesMultiplesDescriptionDimensionsOeuvreRaw")}
                sublabel={t(
                  "estampesMultiplesDescriptionDimensionsOeuvreRawSublabel",
                )}
                expandOnOverflow
                rows={2}
                className="[field-sizing:content] min-h-[3.5rem] resize-none"
              />
            </AF.Col>
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="localisation-historique">
          <AF.Col>
            <MultipleprintHistoryTable
              multipleprintId={multipleprint.id}
              provenanceMode={provenanceMode}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="matrice">
          <AF.Row className="items-start">
            <AF.Col className="w-1/2">
              <AF.Text name="estampesMultiplesMatriceNumeroEnregistrement" />
              <AF.Text name="estampesMultiplesMatriceNumeroInventaire" />
              <ModifiableList
                name="estampesMultiplesMatriceType"
                listKey="liste-types-matrice"
                label={t("estampesMultiplesMatriceType")}
              />
              <FourDimensionalInput
                parentName="estampesMultiplesMatriceDimensions"
                needVolume={false}
                needSurface={false}
              />
              <AF.Text
                name="estampesMultiplesMatriceDimensionsRaw"
                label={t("estampesMultiplesMatriceDimensionsRaw")}
              />
              <AF.Text
                name="estampesMultiplesMatriceWeight"
                unit="kg"
                className="w-20"
                label={t("estampesMultiplesMatriceWeight")}
              />
              <ModifiableList
                name="estampesMultiplesMatriceRangement"
                listKey="liste-storage-locations-matrice"
                label={t("estampesMultiplesMatriceRangement")}
              />
              <AF.Row className="gap-1">
                <AF.Checkbox
                  name="estampesMultiplesMatriceNotLocated"
                  label={t("estampesMultiplesMatriceNotLocated")}
                  align={true}
                />
                <AF.Checkbox
                  name="estampesMultiplesMatriceDestroy"
                  label={t("estampesMultiplesMatriceDestroy")}
                  align={true}
                />
                <AF.Checkbox
                  name="estampesMultiplesMatriceNotPrintable"
                  label={t("estampesMultiplesMatriceNotPrintable")}
                  align={true}
                />
              </AF.Row>
            </AF.Col>
            <AF.Col className="w-1/2">
              <AF.RichText
                name="estampesMultiplesMatriceStateNotes"
                label={t("estampesMultiplesMatriceStateNotes")}
              />
              <AF.RichText
                name="estampesMultiplesMatriceNotes"
                label={t("estampesMultiplesMatriceNotes")}
              />
            </AF.Col>
          </AF.Row>
        </AF.Tab>
        <AF.Tab value="photos-credit-copyright">
          <AF.Col>
            <ModifiableList
              name="estampesMultiplesPhotosCreditOeuvres"
              listKey="liste-credits"
            />
            <ModifiableList
              name="estampesMultiplesPhotosCreditPhotoOeuvres"
              listKey="liste-credits"
            />
            <AF.RichText
              name="estampesMultiplesPhotosCreditRemarques"
              tooltip={t("estampesMultiplesPhotosCreditRemarquesTooltip")}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="epreuveTirage">
          <AF.Col>
            <MultilingualField
              name="estampesMultiplesDescriptionDescriptionCompleteMultilingual"
              render={(lang) => (
                <AF.RichText
                  name={`estampesMultiplesDescriptionDescriptionCompleteMultilingual.${lang}`}
                  label={t("estampesMultiplesDescriptionDescriptionComplete")}
                />
              )}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          value="files"
          headerExtras={
            <AddFileModal
              id={multipleprint.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={multipleprint.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        <AF.Tab value="exemplaires" showHeader={false}>
          <DashboardParamsProviders
            storageKey="exemplaires-multipleprint"
            persistInSearchParams={false}
          >
            <ExemplaireDashboard multipleprintId={multipleprint.id} />
          </DashboardParamsProviders>
        </AF.Tab>
        <AF.Tab value="portfolio" isForm={false}>
          <PortfolioItemsLinks id={multipleprint.id} />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            data={multipleprint}
            id={multipleprint.id}
            type="multipleprint"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedSculptureEditions",
                      label: t("entrysculptureeditions"),
                      type: "link-simple",
                      hidden: !isModuleSculptureEnabled,
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                    {
                      name: "externalLinks",
                      label: t("entryExternalLinks"),
                      type: "link-simple",
                    },
                    {
                      name: "multipleprintInExhibition",
                      label: t("entryexhibitions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionNumeroExpo",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionResume",
                          ),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "expositionsDansEstampesMultiplesDescriptionNumero",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionNumero",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionExemplaireExpose",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleprintInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionReproduitPlanche",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproduitPlanche",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "biblioInMultiPrintDescPlancheNonNum",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionPlancheNonNumerotee",
                              ),
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionCitePage",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionCitePage",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionListePage",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionListePage",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionSousNumero",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionSousNumero",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionPhotographe",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionPhotographe",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionNonPagine",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionNonPagine",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "biblioInMultiPrintDescReproCover",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproductionCouverture",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionTexteLibre",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionTexteLibre",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "multipleprintInAuction",
                      label: t("entryauctions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNumeroVente",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionResume",
                          ),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionNumeroLot",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNumeroLot",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionEstimationBasse",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionEstimationBasse",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionEstimationHaute",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionEstimationHaute",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionVendu",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionVendu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionResultatNonListe",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionResultatNonListe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionNonVenu",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNonVenu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionRetire",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionRetire",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionMiseAPrix",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionMiseAPrix",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionPrixMarteau",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionPrixMarteau",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionPrixAvecFrais",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionPrixAvecFrais",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionExemplaireVendu",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionExemplaireVendu",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditMultiplePrintPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetMultiplePrint(id);

  return (
    <>
      {data && <EditMultipleprint key={data.id} multipleprint={data} />}
      {!data && !isLoading && <h2 className="p-4">estampeNexistePasLabel</h2>}
    </>
  );
};
